import { FlowComponent } from '../../../flow.min.js';
import styles from './styles.js';
import { sessionChannel, hasFeature, hasIACredits, hasLimitAvailable, notify } from '../../../stores/index.js';

const template = FlowComponent.html`
    <div class="lock-wrapper">
        <div id="content" class="locked-content">
            <slot></slot>
        </div>
        <div id="overlay" class="overlay"></div>
        <div id="badge" class="badge-container"></div>
    </div>
`;

export class FlowFeatureLock extends FlowComponent {
    static get styles() { return styles; }
    static get template() { return template; }

    onMount() {
        super.onMount();
        
        // Evaluamos cuando hay cambios en la suscripción
        sessionChannel.bind(this, () => {
            this.#evaluateAccess();
        });

        // Manejador del click para la alerta
        const overlay = this.root.getElementById('overlay');
        overlay.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.#handleLockedClick();
        });

        // Si es inline, permitimos que el click en el badge también dispare la alerta
        const badge = this.root.getElementById('badge');
        badge.style.pointerEvents = 'auto'; // Permitir clicks en el badge
        badge.addEventListener('click', (e) => {
            if (badge.style.display !== 'none') {
                e.preventDefault();
                e.stopPropagation();
                this.#handleLockedClick();
            }
        });
    }

    #getBadgeHTML(level, lockType) {
        let text = `requiere ${level}`;
        let icon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
        
        if (lockType === 'credits') {
            text = 'requiere ia';
            icon = '✨';
        } else if (lockType === 'limit') {
            text = 'límite alcanzado';
            icon = '📈';
        }

        let levelClass = level === 'plus' ? 'level-plus' : 'level-pro';
        if (lockType === 'credits') levelClass = 'level-credits';

        return `
            <span class="lock-badge ${levelClass}" title="Función restringida">
                <span>${text}</span>
                ${icon}
            </span>
        `;
    }

    #evaluateAccess() {
        const oldFeature = this.getAttribute('feature');
        const oldIaAction = this.getAttribute('ia-action');
        
        let lockType = this.getAttribute('lock-type');
        let lockKey = this.getAttribute('lock-key');
        let level = this.getAttribute('level') || 'pro';
        
        // Backwards compatibility
        if (!lockType) {
            if (oldIaAction) {
                lockType = 'credits';
                lockKey = oldIaAction;
            } else if (oldFeature) {
                lockType = 'feature';
                lockKey = oldFeature;
            } else {
                lockType = 'feature';
            }
        }
        
        let hasAccess = true;

        if (lockType === 'feature' && lockKey) {
            hasAccess = hasFeature(lockKey);
        } else if (lockType === 'credits' && lockKey) {
            hasAccess = hasIACredits(lockKey);
        } else if (lockType === 'limit' && lockKey) {
            hasAccess = hasLimitAvailable(lockKey);
        }

        const content = this.root.getElementById('content');
        const overlay = this.root.getElementById('overlay');
        const badge = this.root.getElementById('badge');

        // Si no hay hijos en el slot, no atenuamos nada ni mostramos overlay
        const hasChildren = this.childNodes.length > 0;

        if (hasAccess) {
            content.classList.remove('locked-content');
            overlay.style.display = 'none';
            badge.style.display = 'none';
        } else {
            if (hasChildren) {
                content.classList.add('locked-content');
                overlay.style.display = 'block';
            } else {
                content.classList.remove('locked-content');
                overlay.style.display = 'none';
            }
            badge.style.display = 'block';
            badge.innerHTML = this.#getBadgeHTML(level, lockType);
        }
    }

    #handleLockedClick() {
        let defaultMessage = 'Esta función es exclusiva. Mejora tu plan a PRO para acceder a ella.';
        
        const lockType = this.getAttribute('lock-type');
        const level = this.getAttribute('level') || 'pro';
        const oldIaAction = this.getAttribute('ia-action');
        
        if (lockType === 'credits' || oldIaAction) {
            defaultMessage = 'No tienes suficientes créditos de IA para realizar esta acción. Adquiere más créditos o mejora tu plan.';
        } else if (lockType === 'limit') {
            defaultMessage = 'Has alcanzado el límite de tu plan para este recurso. Mejora tu suscripción para ampliar tus límites.';
        } else if (level === 'plus') {
            defaultMessage = 'Esta función requiere el plan PLUS. Mejora tu suscripción para desbloquearla.';
        }
        
        const message = this.getAttribute('message') || defaultMessage;
        
        // Mostramos el Toast/Callout
        notify(message, 'info');
    }
}

customElements.define('flow-feature-lock', FlowFeatureLock);